interface MessengerConfig {
  sendMessage: (msg: object, targetOrigin: string) => void;
  targetOrigin: string;
}

interface PendingMessage {
  resolve: (data: object) => void;
  reject: (error: Error) => void;
}

// WindowMessenger provides a way to easily send messages
// between windows (e.g., iframe <-> parent, extension <-> idea portal frame)
// with the ability to receive async replies.
export class WindowMessenger {
  private readonly pendingMessages = new Map<string, PendingMessage>();
  private messageIdCounter = 0;
  private messageHandlers: Record<
    string,
    (data: object) => object | Promise<object>
  > = {};

  constructor(private readonly config: MessengerConfig) {
    window.addEventListener('message', this.handleMessage);
  }

  cleanup(): void {
    window.removeEventListener('message', this.handleMessage);
    this.pendingMessages.clear();
    this.messageHandlers = {};
  }

  handleMessage = (event: MessageEvent): void => {
    try {
      const { id, type, ...data } = event.data || {};

      if (event.origin !== this.config.targetOrigin || !id) return;

      // Check if this is a reply to a message we sent
      if (this.pendingMessages.has(id)) {
        const { resolve } = this.pendingMessages.get(id)!;
        this.pendingMessages.delete(id);
        resolve(data);
        return;
      }

      // Handle incoming request
      if (type && this.messageHandlers[type]) {
        const result = this.messageHandlers[type](data);
        Promise.resolve(result).then(replyData => {
          this.config.sendMessage(
            { id, ...(replyData || {}) },
            this.config.targetOrigin
          );
        });
      } else {
        // Send empty acknowledgment
        this.config.sendMessage({ id }, this.config.targetOrigin);
      }
    } catch (error) {
      console.error('Error handling message:', error);
    }
  };

  sendMessage(type: string, data: object = {}): Promise<object> {
    return new Promise((resolve, reject) => {
      const id = `msg_${this.messageIdCounter++}_${Date.now()}`;
      this.pendingMessages.set(id, { resolve, reject });

      this.config.sendMessage({ id, type, ...data }, this.config.targetOrigin);
    });
  }

  onMessage(
    type: string,
    handler: (data: object) => object | Promise<object>
  ): void {
    this.messageHandlers[type] = handler;
  }
}
